import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Search,
  Users,
} from "lucide-react";
import React, { useContext, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import {
  ActionIcon,
  Button,
  Group,
  Select,
  Space,
  Text,
  TextInput,
  useMantineTheme,
} from "@mantine/core";
import Segment from "../services/model/Segment";
import ErrorPage from "../../../common/pages/ErrorPage";
import { getHistoryButtonStyles } from "../../../common/styles/buttonStyles";
import ProgressIndicator from "../../../themes/components/ProgressIndicator";
import useSegmentListApiService from "../services/api/SegmentListApiService";
import { AuthContext } from "../../../providers/AuthProvider";
import SegmentEmptyState from "./SegmentEmptyState";
import DirectoryTable from "../../../themes/components/DirectoryTable";
import ResponsiveHeight from "../../../utils/ResponsiveHeight";
import SegmentStatusBadge from "../elements/SegmentStatusBadge";
import SegmentActionButtons from "../elements/SegmentActionButtons";

type SegmentTableProps = {
  onView: (segment: Segment) => void;
  onViewEmployees: (segment: Segment) => void;
  onCreateSegment: () => void;
  onViewHistory: () => void;
};

const SegmentTable: React.FC<SegmentTableProps> = ({
  onView,
  onViewEmployees,
  onCreateSegment,
  onViewHistory,
}) => {
  const auth = useContext(AuthContext);
  const theme = useMantineTheme();
  const tableMaxHeight = ResponsiveHeight(
    "",
    "70vh",
    "70vh",
    "70vh",
    "56vh",
    "70vh"
  );

  const [search, setSearch] = useState("");
  const [searchKeyword, setSearchKeyword] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState("10");

  const pageSize = parseInt(rowsPerPage, 10);

  const { isError, isLoading, data, error } = useSegmentListApiService(
    {
      employer: auth?.employer?.id ?? 0,
      search,
      page,
      page_size: pageSize,
      include_match_count: true,
    },
    Boolean(auth?.employer?.id)
  );

  // Debounced search - triggers 500ms after user stops typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchKeyword);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchKeyword]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  if (!auth) {
    return <Navigate replace to="/login" />;
  }

  if (isLoading) {
    return <ProgressIndicator />;
  }

  if (isError) {
    return <ErrorPage errors={error} />;
  }

  if (!data) {
    return <SegmentEmptyState onCreateClick={onCreateSegment} />;
  }

  if (!data.results.length && !search.length) {
    return <SegmentEmptyState onCreateClick={onCreateSegment} />;
  }

  // Show "No results" message when search is active but no results found
  const showNoResults = !data.results.length && search.length > 0;

  // Pagination calculations
  const totalCount = data?.count || 0;
  const totalPages = data?.pages || 1;
  const startItem = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const endItem = Math.min(page * pageSize, totalCount);

  return (
    <>
      <Space h="xl" />
      <Group position="apart" noWrap>
        <TextInput
          icon={<Search size={16} />}
          placeholder="Search segments by name or description..."
          value={searchKeyword}
          onChange={(e) => setSearchKeyword(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              setSearch(searchKeyword);
            }
          }}
          radius="sm"
          style={{ flex: 1, maxWidth: 504 }}
          styles={{
            input: {
              borderColor: "#cdd3d6",
            },
          }}
        />
        <Group spacing="md" noWrap>
          <Button
            variant="outline"
            type="button"
            onClick={onViewHistory}
            styles={getHistoryButtonStyles(theme)}
          >
            View History Updates
          </Button>
          <Button
            variant="filled"
            type="button"
            radius={8}
            onClick={onCreateSegment}
            styles={{
              root: {
                backgroundColor: "#062133",
                fontWeight: 600,
                "&:hover": {
                  backgroundColor: "#0d3347",
                },
              },
            }}
          >
            Create New Segment
          </Button>
        </Group>
      </Group>
      <Space h="md" />
      {showNoResults ? (
        <Text color="dimmed" size="sm" align="center" py="xl">
          No segments found matching your search.
        </Text>
      ) : (
        <DirectoryTable<Segment>
          tableMaxHeight={tableMaxHeight}
          data={data.results}
          selection={[]}
          // eslint-disable-next-line @typescript-eslint/no-empty-function
          onSelect={() => {}}
          hideSelectControls
          headers={[
            "Segment Name",
            "Description",
            "No. of Employees",
            "No. of Rules",
            "Status",
            "",
          ]}
          rows={data.results.map((segment) => {
            const INACTIVE_STATUSES = ["expired", "closed"];
            const isInUse = segment.usages
              ? segment.usages.announcements.some(
                  (a) => !INACTIVE_STATUSES.includes(a.status)
                ) ||
                segment.usages.forms.some(
                  (f) => !INACTIVE_STATUSES.includes(f.status)
                ) ||
                segment.usages.ewa_blocking.some((e) => e.is_active)
              : segment.assignment_count > 0;
            return {
              name: segment.name,
              description: (
                <span
                  style={{
                    display: "block",
                    maxWidth: 200,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    color: "#868e96",
                  }}
                >
                  {segment.description}
                </span>
              ),
              employeeCount: (
                <Group spacing={6} noWrap>
                  <Text size="sm">{segment.matched_employee_count ?? 0}</Text>
                  <ActionIcon
                    size="xs"
                    variant="transparent"
                    title="View employees"
                    onClick={() => onViewEmployees(segment)}
                    style={{ color: "#868e96" }}
                  >
                    <Users size={14} />
                  </ActionIcon>
                </Group>
              ),
              rulesCount: segment.rules.length,
              status: <SegmentStatusBadge isActive={isInUse} />,
              actions: (
                <SegmentActionButtons segment={segment} onView={onView} />
              ),
            };
          })}
        />
      )}
      {totalCount > 0 && (
        <>
          <Space h="md" />
          <Group position="right" spacing="md">
            <Group spacing="xs">
              <Text size="sm">Rows per page:</Text>
              <Select
                value={rowsPerPage}
                onChange={(val) => {
                  setRowsPerPage(val || "10");
                  setPage(1);
                }}
                data={[
                  { value: "10", label: "10" },
                  { value: "25", label: "25" },
                  { value: "50", label: "50" },
                ]}
                size="xs"
                style={{ width: 70 }}
                rightSection={<ChevronDown size={12} />}
              />
            </Group>
            <Text size="sm">
              {startItem}-{endItem} of {totalCount}
            </Text>
            <Group spacing="md">
              <ActionIcon
                size="sm"
                variant="transparent"
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
                style={{ opacity: page === 1 ? 0.4 : 1 }}
                aria-label="Previous page"
              >
                <ChevronLeft size={14} />
              </ActionIcon>
              <ActionIcon
                size="sm"
                variant="transparent"
                disabled={page >= totalPages}
                onClick={() => setPage(page + 1)}
                style={{ opacity: page >= totalPages ? 0.4 : 1 }}
                aria-label="Next page"
              >
                <ChevronRight size={14} />
              </ActionIcon>
            </Group>
          </Group>
        </>
      )}
    </>
  );
};

export default SegmentTable;
